import React from 'react';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { BinConstraint } from '../types/bin_constraints/binConstraint';
import { BinConstraintContext, BinConstraintContextProps } from '../context/BinConstraintContext';

const binConstraintState = {
  items: new Map<string, BinConstraint>(),
  loading: false,
};

export const binConstraintSlice = createSlice({
  name: 'binConstraint',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      binConstraintState.items = new Map();
      binConstraintState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<BinConstraint[]>) => {
      binConstraintState.items = new Map();
      action.payload.forEach(p => binConstraintState.items.set(p.id, new BinConstraint(p)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<BinConstraint[]>) => {
      action.payload.forEach(p => binConstraintState.items.set(p.id, new BinConstraint(p)));
      binConstraintState.items = new Map(binConstraintState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<BinConstraint[]>) => {
      action.payload.forEach(p => binConstraintState.items.delete(p.id));
      binConstraintState.items = new Map(binConstraintState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      binConstraintState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface BinConstraintProviderProps {
  children: React.ReactNode;
}

export default function BinConstraintProvider({ children }: BinConstraintProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.binContraints.key);

  const validateBinConstraints = (stockLocationId: string, binId: string, pmdId: string) => {
    const pmdConstraintForStockLocation = [...binConstraintState.items.values()].find(
      c => c.stockLocationId === stockLocationId && c.pmdId === pmdId,
    );
    return !pmdConstraintForStockLocation || pmdConstraintForStockLocation.binId === binId;
  };

  const binConstraintContext: BinConstraintContextProps = {
    validateBinConstraints,
    binConstraints: binConstraintState.items,
    binConstraintsLoading: binConstraintState.loading,
    setBinConstraints: (contraints: Map<string, BinConstraint>) => {
      dispatch(binConstraintSlice.actions.set([...contraints.values()]));
    },
  };

  return <BinConstraintContext.Provider value={binConstraintContext}>{children}</BinConstraintContext.Provider>;
}
